import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Divider, Dialog, DialogTitle, DialogContent, Grid, CardHeader, IconButton, TextField, Button, Chip, Select, MenuItem, FormControl, InputLabel} from '@material-ui/core';
import { FileCopy as CopyIcon, Add as AddIcon, Close as CloseIcon } from '@material-ui/icons';
import LogoCavca from 'assets/Branding/cavcaLogoV2White.png';
import LogoAzul from 'assets/Branding/cavcaLogoV2.png';
import AxaLogo from 'assets/InsuresV2/axa.png';
import SuraLogo from 'assets/InsuresV2/sura.png';
import BolivarLogo from 'assets/InsuresV2/bolivar.png';
import MapfreLogo from 'assets/InsuresV2/mapfre.png';
import SolidariaLogo from 'assets/InsuresV2/solidaria.png';
import LibertyLogo from 'assets/InsuresV2/liberty.png';
import HDI from 'assets/InsuresV2/hdi.png';
import EstadoLogo from 'assets/InsuresV2/estado.png';
import ZurichLogo from 'assets/InsuresV2/zurich.png';
import EquidadLogo from 'assets/InsuresV2/equidad.png';
import PrevisoraLogo from 'assets/InsuresV2/previsora.png';
import SBSLogo from 'assets/InsuresV2/sbs.png';
import QualitasLogo from 'assets/InsuresV2/qualitas.png';
import { useStyles } from './styles';
import { getLinkOffer, getInsuranceRate, getAvailableProducts, getProductDetails } from 'services/QuotationService';
import { validAndParseNumber, formatCurrency as formatCurrencyUtil } from 'utils/utils';
import InsuranceLogoV2 from '../InsuranceLogo/InsuranceLogoV2';
import html2pdf from 'html2pdf.js';

const generateUniqueId = (quote: any): string => {
  return `${quote.insurerKey}-${quote.title}-${quote.licenseVehicle}`;
};

// Agregar el objeto de mapeo de logos después de los imports
const insuranceLogos: { [key: string]: string } = {
  AXA: AxaLogo,
  SURA: SuraLogo,
  BOLIVAR: BolivarLogo,
  MAPFRE: MapfreLogo,
  SOLIDARIA: SolidariaLogo,
  LIBERTY: LibertyLogo,
  HDI: HDI,
  SEGUROS_DEL_ESTADO: EstadoLogo,
  ESTADO: EstadoLogo,
  ZURICH: ZurichLogo,
  EQUIDAD: EquidadLogo,
  PREVISORA: PrevisoraLogo,
  SBS: SBSLogo,
  QUALITAS: QualitasLogo
};

// Función auxiliar para transformar valores Si/No
const transformValue = (value: string): string => {
  if (!value) return "N/A";
  
  const normalizedValue = value.toLowerCase().trim();
  if (["si", "sí", "yes"].includes(normalizedValue)) {
    return "+";
  } else if (["no", "not", "x", "X"].includes(normalizedValue)) {
    return "×";
  }
  return value;
};

// Función para formatear valores monetarios
const formatCurrency = (value: string): string => {
  if (!value) return "N/A";
  const cleanValue = value.replace(/[$.]/g, '').trim();
  const number = parseFloat(cleanValue.replace(/,/g, ''));
  if (isNaN(number)) return value;
  return `$ ${number.toLocaleString('es-CO')}`;
};

// Función para formatear valores monetarios (solo cotizaciones manuales)
const formatCurrencyValueForDisplay = (value: string | number, isManual: boolean = false): string => {
  if (!value) return "N/A";
  
  const stringValue = value.toString();
  
  // Si no es manual y ya tiene formato, lo devolvemos como está
  if (!isManual && (stringValue.includes('.') || stringValue.includes(','))) {
    return stringValue;
  }
  
  // Si es manual o no tiene formato, lo formateamos
  const numericValue = typeof value === 'string' ? 
    parseFloat(value.replace(/[^0-9]/g, '')) : value;
  if (isNaN(numericValue)) return "N/A";
  return numericValue.toLocaleString('es-CO');
};

// Función para mapear nombres de aseguradoras del API a las keys del componente InsuranceLogoV2
const mapInsurerKeyToLogoKey = (insurerKey: string): string => {
  if (!insurerKey) return '';
  
  // Normalizar el nombre de la aseguradora (convertir a mayúsculas)
  const normalized = insurerKey.toUpperCase().trim();
  
  const mappings: { [key: string]: string } = {
    'AXA': 'AXA',
    'BOLIVAR': 'BOLIVAR', 
    'EQUIDAD': 'EQUIDAD',
    'HDI': 'HDI',        // Para "Hdi" → "HDI"
    'MAPFRE': 'MAPFRE',
    'SBS': 'SBS',
    'SURA': 'SURA',
    'LIBERTY': 'LIBERTY',
    'ZURICH': 'ZURICH',
    'PREVISORA': 'PREVISORA',
    'SOLIDARIA': 'SOLIDARIA',
    'SEGUROS_DEL_ESTADO': 'ESTADO',
    'ESTADO': 'ESTADO',
    'QUALITAS': 'QUALITAS'
  };
  
  return mappings[normalized] || normalized;
};

// Función para ofuscar el número de documento
const maskDocument = (document: string): string => {
  if (!document) return "N/A";
  const docString = document.toString();
  if (docString.length <= 3) return docString;
  
  const firstPart = docString.slice(0, 2);
  const lastPart = docString.slice(-2);
  const maskedPart = '*'.repeat(docString.length - 4);
  return `${firstPart}${maskedPart}${lastPart}`;
};

const QuotePreviewModal = ({ open, onClose, selectedQuotes, installmentPayments }: { open: boolean, onClose: () => void, selectedQuotes: any[], installmentPayments: { [key: string]: string } }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [offerLink, setOfferLink] = useState<string>('');
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [showManualQuoteForm, setShowManualQuoteForm] = useState<boolean>(false);
  const [calculatingInstallment, setCalculatingInstallment] = useState<boolean>(false);
  const [loadingProductDetails, setLoadingProductDetails] = useState<boolean>(false);
  const [localSelectedQuotes, setLocalSelectedQuotes] = useState<any[]>([]);
  const [localInstallmentPayments, setLocalInstallmentPayments] = useState<{ [key: string]: string }>({});
  const [manualQuote, setManualQuote] = useState({
    insurerKey: '',
    segmentKey: '',
    productKey: '',
    value: '',
    installmentPayment: '',
    title: '',
    firstName: selectedQuotes[0]?.firstName || '',
    licenseVehicle: selectedQuotes[0]?.licenseVehicle || '',
    nDocument: selectedQuotes[0]?.nDocument || '',
    cityCirculation: selectedQuotes[0]?.cityCirculation || [],
    modelo: selectedQuotes[0]?.modelo || '',
    valueInsured: selectedQuotes[0]?.valueInsured || '',
    service: selectedQuotes[0]?.service || '',
    ref1: selectedQuotes[0]?.ref1 || '',
    ref2: selectedQuotes[0]?.ref2 || '',
    ref3: selectedQuotes[0]?.ref3 || '',
    segment: {
      segment: '',
      extracontractualCivilLiability: {
        visible: true,
        items: [
          { key: 'danosBienesTerceros', label: 'Daños a Bienes de Terceros', value: 'Si', visible: true },
          { key: 'muerteLesionUnaPersona', label: 'Muerte o Lesión a Una Persona', value: 'Si', visible: true },
          { key: 'muerteLesionDosOMasPersonas', label: 'Muerte o Lesión a Dos o Más Personas', value: 'Si', visible: true }
        ]
      },
      coverages: {
        visible: true,
        items: []
      },
      assistances: {
        visible: true,
        items: []
      },
      additionalAssistances: {
        visible: true,
        items: []
      }
    }
  });

  // Div oculto para pre-renderizar los logos
  const logoRef = React.useRef<HTMLDivElement>(null);
  
  // Referencia para hacer scroll al enlace generado
  const offerLinkRef = React.useRef<HTMLDivElement>(null);

  const handleGenerateOffer = async () => {
    setLoading(true);
  
    const selectedProducts = localSelectedQuotes.map((quote) => {
      const uniqueId = generateUniqueId(quote);
      return {
        ...quote,
        installmentPayment: localInstallmentPayments[uniqueId] || 'Calculando...',
      };
    });

    console.log('Productos ' + JSON.stringify(selectedProducts))
  
    try {
      const { linkApp } = await getLinkOffer({selectedProducts});
      if (linkApp) {
        setOfferLink(linkApp);
        
        // Hacer scroll hacia la sección del enlace generado después de un breve delay
        setTimeout(() => {
          if (offerLinkRef.current) {
            offerLinkRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 500);
      }
    } catch (error) {
      console.error("Error en el servicio de generación de oferta:", error);
    }
    setLoading(false);
  };
  
  const handleCopyLink = (link: string) => {
    navigator.clipboard.writeText(link).then(() => {
      alert("Enlace copiado");
    }).catch((err) => {
      console.error("Error al copiar el enlace: ", err);
    });
  };
  const generatePDF = () => {
    // Validar número máximo de aseguradoras
    if (localSelectedQuotes.length > 9) {
      alert('No se puede generar el PDF con más de 9 aseguradoras. Por favor, seleccione un máximo de 9 aseguradoras para generar la oferta en PDF.');
      return;
    }

    // Función para calcular tamaños dinámicos basados en el número de aseguradoras
    const calculateSizes = (numInsurers: number) => {
      const baseSizes = {
        logoWidth: 100,
        logoHeight: 40,
        titleFontSize: 20,
        infoFontSize: 12,
        tableFontSize: 11,
        padding: 10,
        margin: 15
      };

      // Ajustar tamaños según el número de aseguradoras
      if (numInsurers > 6) {
        return {
          logoWidth: 80,
          logoHeight: 32,
          titleFontSize: 16,
          infoFontSize: 10,
          tableFontSize: 9,
          padding: 6,
          margin: 10
        };
      } else if (numInsurers > 4) {
        return {
          logoWidth: 90,
          logoHeight: 36,
          titleFontSize: 18,
          infoFontSize: 11,
          tableFontSize: 10,
          padding: 8,
          margin: 12
        };
      }

      return baseSizes;
    };

    const sizes = calculateSizes(localSelectedQuotes.length);
    
    // Crear un elemento temporal para el PDF
    const element = document.createElement('div');
    element.style.padding = '2px';
    element.style.fontFamily = 'Arial, sans-serif';
    
    // Header con logo y título
    const header = document.createElement('div');
    header.style.display = 'flex';
    header.style.justifyContent = 'space-between';
    header.style.alignItems = 'center';
    header.style.marginBottom = '15px';
    header.style.padding = '10px';
    header.style.backgroundColor = '#F8F9FA';
    header.style.borderRadius = '8px';
    header.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
    
    // Logo
    const logoContainer = document.createElement('div');
    logoContainer.style.display = 'flex';
    logoContainer.style.alignItems = 'center';
    logoContainer.style.gap = '10px';
    const logo = document.createElement('img');
    logo.src = LogoAzul;
    logo.style.width = '100px';
    logo.style.height = '40px';
    logo.style.objectFit = 'contain';
    logoContainer.appendChild(logo);
    
    // Título
    const title = document.createElement('h1');
    title.style.color = '#02CBF4';
    title.style.fontSize = '20px';
    title.style.fontWeight = 'bold';
    title.style.margin = '0';
    title.style.flex = '1';
    title.style.textAlign = 'center';
    title.textContent = 'Cuadro Comparativo de Seguros';
    
    header.appendChild(logoContainer);
    header.appendChild(title);
    element.appendChild(header);
    
    // Información del cliente
    const clientInfo = document.createElement('div');
    clientInfo.style.marginBottom = '5px';
    clientInfo.style.padding = '5px';
    clientInfo.style.backgroundColor = '#F8F9FA';
    clientInfo.style.borderRadius = '8px';
    
    const clientTitle = document.createElement('h2');
    clientTitle.style.color = '#02CBF4';
    clientTitle.style.fontSize = '10px';
    clientTitle.style.marginBottom = '5px';
    clientTitle.textContent = 'Información del Asegurado';
    
    const clientDetails = document.createElement('div');
    clientDetails.style.display = 'flex';
    clientDetails.style.flexWrap = 'wrap';
    clientDetails.style.gap = '5px';
    clientDetails.innerHTML = `
      <div>
        <p style="color: #666; font-size: ${sizes.infoFontSize}px; margin: 0;">Nombre del Asegurado</p>
        <p style="color: #333; font-size: ${sizes.infoFontSize}px; margin: 3px 0 0 0; font-weight: bold;">${localSelectedQuotes[0].firstName}</p>
      </div>
      <div>
        <p style="color: #666; font-size: ${sizes.infoFontSize}px; margin: 0;">Placa del Vehículo</p>
        <p style="color: #333; font-size: ${sizes.infoFontSize}px; margin: 3px 0 0 0; font-weight: bold;">${localSelectedQuotes[0].licenseVehicle}</p>
      </div>
      <div>
        <p style="color: #666; font-size: ${sizes.infoFontSize}px; margin: 0;">Documento</p>
        <p style="color: #333; font-size: ${sizes.infoFontSize}px; margin: 3px 0 0 0; font-weight: bold;">${maskDocument(localSelectedQuotes[0].nDocument.toString())}</p>
      </div>
      <div>
        <p style="color: #666; font-size: ${sizes.infoFontSize}px; margin: 0;">Ciudad de Circulación</p>
        <p style="color: #333; font-size: ${sizes.infoFontSize}px; margin: 3px 0 0 0; font-weight: bold;">${localSelectedQuotes[0].cityCirculation[0].split('-').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</p>
      </div>
      <div>
        <p style="color: #666; font-size: ${sizes.infoFontSize}px; margin: 0;">Modelo</p>
        <p style="color: #333; font-size: ${sizes.infoFontSize}px; margin: 3px 0 0 0; font-weight: bold;">${localSelectedQuotes[0].modelo}</p>
      </div>
      <div>
        <p style="color: #666; font-size: ${sizes.infoFontSize}px; margin: 0;">Valor Asegurado</p>
        <p style="color: #333; font-size: ${sizes.infoFontSize}px; margin: 3px 0 0 0; font-weight: bold;">${formatCurrency(localSelectedQuotes[0].tVehicle === 'usado' ? localSelectedQuotes[0].valueInsured.toString() + '000' : localSelectedQuotes[0].valueInsured.toString())}</p>
      </div>
      <div>
        <p style="color: #666; font-size: ${sizes.infoFontSize}px; margin: 0;">Servicio</p>
        <p style="color: #333; font-size: ${sizes.infoFontSize}px; margin: 3px 0 0 0; font-weight: bold;">${localSelectedQuotes[0].service.charAt(0).toUpperCase() + localSelectedQuotes[0].service.slice(1)}</p>
      </div>
      <div>
        <p style="color: #666; font-size: ${sizes.infoFontSize}px; margin: 0;">Referencia</p>
        <p style="color: #333; font-size: ${sizes.infoFontSize}px; margin: 3px 0 0 0; font-weight: bold;">${localSelectedQuotes[0].ref1} ${localSelectedQuotes[0].ref2} ${localSelectedQuotes[0].ref3}</p>
      </div>
    `;
    
    clientInfo.appendChild(clientTitle);
    clientInfo.appendChild(clientDetails);
    element.appendChild(clientInfo);
    
    // Tabla de comparación
    const table = document.createElement('table');
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
    table.style.marginTop = '5px';
    table.style.pageBreakInside = 'auto';
    
    // Headers
    const headerRow = document.createElement('tr');
    headerRow.style.backgroundColor = '#ffffff';
    headerRow.style.pageBreakInside = 'avoid';
    headerRow.style.pageBreakAfter = 'auto';
    
    const firstHeader = document.createElement('th');
    firstHeader.style.padding = '2px';
    firstHeader.style.textAlign = 'left';
    firstHeader.style.borderBottom = '2px solid #02CBF4';
    firstHeader.style.width = selectedQuotes.length <= 4 ? '25%' : '12%';
    firstHeader.textContent = '';
    headerRow.appendChild(firstHeader);
    
    // Calcular el ancho de las columnas de aseguradoras
    const insurerColumnWidth = `${(localSelectedQuotes.length <= 4 ? 75 : 88) / localSelectedQuotes.length}%`;
    
    localSelectedQuotes.forEach(quote => {
      const th = document.createElement('th');
      th.style.padding = '2px';
      th.style.textAlign = 'center';
      th.style.borderBottom = '2px solid #02CBF4';
      th.style.width = insurerColumnWidth;
      
      // Contenedor para logo y título
      const container = document.createElement('div');
      container.style.display = 'flex';
      container.style.flexDirection = 'column';
      container.style.alignItems = 'center';
      container.style.gap = '2px';
      
      // Logo
      const logo = document.createElement('img');
      logo.src = insuranceLogos[quote.insurerKey.toUpperCase()];
      logo.style.width = localSelectedQuotes.length <= 4 ? '60px' : '35px';
      logo.style.height = localSelectedQuotes.length <= 4 ? '30px' : '18px';
      logo.style.objectFit = 'contain';
      
      // Título
      const title = document.createElement('span');
      title.textContent = quote.insuranceDetails?.nameProduct || quote.segment?.insuranceDetails?.nameProduct || quote.title || "";
      title.style.fontSize = `${sizes.tableFontSize}px`;
      title.style.color = '#02CBF4';
      title.style.fontWeight = 'bold';
      title.style.maxWidth = '100%';
      title.style.overflow = 'hidden';
      title.style.textOverflow = 'ellipsis';
      title.style.whiteSpace = 'normal';
      title.style.textAlign = 'center';
      title.style.lineHeight = '1.2';
      title.style.maxHeight = localSelectedQuotes.length <= 4 ? 'none' : '2.4em';
      title.style.display = '-webkit-box';
      title.style.webkitLineClamp = localSelectedQuotes.length <= 4 ? 'unset' : '2';
      title.style.webkitBoxOrient = 'vertical';
      
      container.appendChild(logo);
      container.appendChild(title);
      th.appendChild(container);
      headerRow.appendChild(th);
    });
    
    table.appendChild(headerRow);
    
    // Fila azul de INFORMACIÓN BÁSICA
    const blueHeaderRow = document.createElement('tr');
    blueHeaderRow.style.pageBreakInside = 'avoid';
    blueHeaderRow.style.pageBreakAfter = 'auto';
    const blueHeaderCell = document.createElement('td');
    blueHeaderCell.colSpan = localSelectedQuotes.length + 1;
    blueHeaderCell.style.backgroundColor = '#02CBF4';
    blueHeaderCell.style.color = 'white';
    blueHeaderCell.style.padding = '8px'; // Reducido de 10px a 8px
    blueHeaderCell.style.fontWeight = 'bold';
    blueHeaderCell.style.fontSize = '12px'; // Reducido de 13px a 12px
    blueHeaderCell.textContent = 'INFORMACIÓN BÁSICA';
    blueHeaderRow.appendChild(blueHeaderCell);
    table.appendChild(blueHeaderRow);
    
    // Agregar Prima Total y Deducible antes de las secciones
    const primaRow = document.createElement('tr');
    primaRow.style.pageBreakInside = 'avoid';
    primaRow.style.pageBreakAfter = 'auto';
    const primaLabelCell = document.createElement('td');
    primaLabelCell.style.padding = '8px'; // Reducido de 12px a 8px
    primaLabelCell.style.backgroundColor = '#F8F9FA';
    primaLabelCell.style.fontWeight = 'bold';
    primaLabelCell.style.fontSize = '11px'; // Reducido de 12px a 11px
    primaLabelCell.style.color = '#333';
    primaLabelCell.style.width = '20%'; // Reducido de 25% a 20%
    primaLabelCell.textContent = 'Prima Total';
    primaRow.appendChild(primaLabelCell);

    localSelectedQuotes.forEach(quote => {
      const primaValueCell = document.createElement('td');
      primaValueCell.style.padding = '8px';
      primaValueCell.style.textAlign = 'center';
      primaValueCell.style.fontSize = '13px';
      primaValueCell.style.color = '#02CBF4';
      primaValueCell.style.fontWeight = 'bold';
      primaValueCell.style.width = insurerColumnWidth;
      primaValueCell.style.whiteSpace = 'nowrap';
      primaValueCell.style.minWidth = '90px';
      primaValueCell.textContent = formatCurrency(quote.value);
      primaRow.appendChild(primaValueCell);
    });
    table.appendChild(primaRow);

    // Agregar fila de Pagos 11 Cuotas
    const installmentRow = document.createElement('tr');
    installmentRow.style.pageBreakInside = 'avoid';
    installmentRow.style.pageBreakAfter = 'auto';
    const installmentLabelCell = document.createElement('td');
    installmentLabelCell.style.padding = '8px';
    installmentLabelCell.style.backgroundColor = '#F8F9FA';
    installmentLabelCell.style.fontWeight = 'bold';
    installmentLabelCell.style.fontSize = '11px';
    installmentLabelCell.style.color = '#333';
    installmentLabelCell.style.width = '20%';
    installmentLabelCell.textContent = 'Pagos 11 Cuotas';
    installmentRow.appendChild(installmentLabelCell);

    localSelectedQuotes.forEach(quote => {
      const uniqueId = generateUniqueId(quote);
      const installmentValueCell = document.createElement('td');
      installmentValueCell.style.padding = '8px';
      installmentValueCell.style.textAlign = 'center';
      installmentValueCell.style.fontSize = '13px';
      installmentValueCell.style.color = '#02CBF4';
      installmentValueCell.style.fontWeight = 'bold';
      installmentValueCell.style.width = insurerColumnWidth;
      installmentValueCell.textContent = formatCurrency(localInstallmentPayments[uniqueId] || 'Calculando...');
      installmentRow.appendChild(installmentValueCell);
    });
    table.appendChild(installmentRow);

    const deducibleRow = document.createElement('tr');
    deducibleRow.style.pageBreakInside = 'avoid';
    deducibleRow.style.pageBreakAfter = 'auto';
    const deducibleLabelCell = document.createElement('td');
    deducibleLabelCell.style.padding = '8px'; // Reducido de 12px a 8px
    deducibleLabelCell.style.backgroundColor = '#F8F9FA';
    deducibleLabelCell.style.fontWeight = 'bold';
    deducibleLabelCell.style.fontSize = '11px'; // Reducido de 12px a 11px
    deducibleLabelCell.style.color = '#333';
    deducibleLabelCell.style.width = '20%'; // Reducido de 25% a 20%
    deducibleLabelCell.textContent = 'Deducible';
    deducibleRow.appendChild(deducibleLabelCell);

    localSelectedQuotes.forEach(quote => {
      const deducibleValueCell = document.createElement('td');
      deducibleValueCell.style.padding = '8px'; // Reducido de 12px a 8px
      deducibleValueCell.style.textAlign = 'center';
      deducibleValueCell.style.fontSize = '11px'; // Reducido de 12px a 11px
      deducibleValueCell.style.color = '#666';
      deducibleValueCell.style.width = insurerColumnWidth;
      deducibleValueCell.textContent = quote.segment?.extracontractualCivilLiability?.deducible || "N/A";
      deducibleRow.appendChild(deducibleValueCell);
    });
    table.appendChild(deducibleRow);
    
    // Definir las secciones
    interface Section {
      title: string;
      items: Array<{
        label: string;
        value: (quote: any) => string;
      }>;
    }

    // Función para obtener todos los items únicos de una sección
    const getUniqueItems = (quotes: any[], sectionKey: string) => {
      console.log(quotes);
      const items = new Set<string>();
      quotes.forEach(quote => {
        quote.segment?.[sectionKey]?.items?.forEach((item: any) => {
          if (item.visible) {
            items.add(item.label);
          }
        });
      });
      return Array.from(items);
    };

    // Obtener todos los items únicos de cada sección
    const uniqueCoverageItems = getUniqueItems(localSelectedQuotes, 'coverages');
    const uniqueAssistanceItems = getUniqueItems(localSelectedQuotes, 'assistances');
    const uniqueAdditionalAssistanceItems = getUniqueItems(localSelectedQuotes, 'additionalAssistances');

    const sections: Section[] = [
      {
        title: 'RESPONSABILIDAD CIVIL EXTRACONTRACTUAL',
        items: [
          { label: 'Daños a Bienes de Terceros', value: (quote: any) => transformValue(quote.segment?.extracontractualCivilLiability?.items?.find((item: any) => item.key === "danosBienesTerceros")?.value || "N/A") },
          { label: 'Muerte o Lesión a Una Persona', value: (quote: any) => transformValue(quote.segment?.extracontractualCivilLiability?.items?.find((item: any) => item.key === "muerteLesionUnaPersona")?.value || "N/A") },
          { label: 'Muerte o Lesión a Dos o Más Personas', value: (quote: any) => transformValue(quote.segment?.extracontractualCivilLiability?.items?.find((item: any) => item.key === "muerteLesionDosOMasPersonas")?.value || "N/A") }
        ]
      },
      {
        title: 'COBERTURAS AL VEHÍCULO',
        items: uniqueCoverageItems.map(label => ({
          label,
          value: (quote: any) => transformValue(quote.segment?.coverages?.items?.find((item: any) => item.label === label)?.value || "N/A")
        }))
      },
      {
        title: 'ASISTENCIAS',
        items: uniqueAssistanceItems.map(label => ({
          label,
          value: (quote: any) => transformValue(quote.segment?.assistances?.items?.find((item: any) => item.label === label)?.value || "N/A")
        }))
      },
      {
        title: 'ASISTENCIAS ADICIONALES',
        items: uniqueAdditionalAssistanceItems.map(label => ({
          label,
          value: (quote: any) => transformValue(quote.segment?.additionalAssistances?.items?.find((item: any) => item.label === label)?.value || "N/A")
        }))
      }
    ];
    
    sections.forEach(section => {
      // Título de sección
      const sectionRow = document.createElement('tr');
      sectionRow.style.pageBreakInside = 'avoid';
      sectionRow.style.pageBreakAfter = 'auto';
      const sectionCell = document.createElement('td');
      sectionCell.colSpan = localSelectedQuotes.length + 1;
      sectionCell.style.backgroundColor = '#02CBF4';
      sectionCell.style.color = 'white';
      sectionCell.style.padding = '10px';
      sectionCell.style.fontWeight = 'bold';
      sectionCell.style.fontSize = '13px';
      sectionCell.textContent = section.title;
      sectionRow.appendChild(sectionCell);
      table.appendChild(sectionRow);
      
      // Items de la sección
      section.items.forEach((item: { label: string; value: (quote: any) => string }) => {
        const row = document.createElement('tr');
        row.style.pageBreakInside = 'avoid';
        row.style.pageBreakAfter = 'auto';
        const labelCell = document.createElement('td');
        labelCell.style.padding = '12px';
        labelCell.style.backgroundColor = '#F8F9FA';
        labelCell.style.fontWeight = 'bold';
        labelCell.style.fontSize = '11px';
        labelCell.style.color = '#333';
        labelCell.style.width = '25%';
        labelCell.textContent = item.label;
        row.appendChild(labelCell);
        
        localSelectedQuotes.forEach(quote => {
          const valueCell = document.createElement('td');
          valueCell.style.padding = '2px';
          valueCell.style.textAlign = 'center';
          valueCell.style.fontSize = '8px';
          valueCell.style.width = insurerColumnWidth;
          const value = item.value(quote);
          if (value === '+') {
            valueCell.style.color = '#28a745';
            valueCell.style.fontWeight = 'bold';
            valueCell.style.fontSize = '14px'; // Aumentado de 11px a 14px
            valueCell.textContent = '✓'; // Cambiado de + a ✓
          } else if (value === '×') {
            valueCell.style.color = '#dc3545';
            valueCell.style.fontWeight = 'bold';
            valueCell.style.fontSize = '14px'; // Aumentado de 11px a 14px
            valueCell.textContent = '×';
          } else {
            valueCell.style.color = '#666';
            valueCell.style.fontSize = '8px';
            valueCell.textContent = value;
          }
          row.appendChild(valueCell);
        });
        
        table.appendChild(row);
      });
    });
    
    element.appendChild(table);
    
    // Footer
    const footer = document.createElement('div');
    footer.style.marginTop = '30px';
    footer.style.padding = '15px';
    footer.style.borderTop = '1px solid #eee';
    footer.style.fontSize = '10px';
    footer.style.color = '#666';
    footer.innerHTML = `
      <p style="margin: 0;">Esta cotización tiene una duración de 10 días.</p>
      <p style="margin: 0;">Las condiciones, valores y coberturas indicadas pueden variar según ajustes en tarifas, políticas de la aseguradora y cualquier cambio en la información proporcionada en la solicitud.</p>
      <p style="margin: 5px 0 0 0;">Generado por CAVCA - ${new Date().toLocaleDateString()}</p>
    `;
    element.appendChild(footer);
    
    // Configuración de html2pdf
    const opt = {
      margin: 0.1,
      filename: `Cotizacion_${localSelectedQuotes[0].licenseVehicle}.pdf`,
      image: { 
        type: 'jpeg', 
        quality: 1 
      },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true
      },
      jsPDF: { 
        unit: 'in', 
        format: 'a4', 
        orientation: localSelectedQuotes.length > 4 ? 'landscape' as const : 'portrait' as const
      }
    };
    
    // Generar PDF
    html2pdf().set(opt).from(element).save();
  };
  
  useEffect(() => {
    if (!open) {
      setOfferLink('');
    } else {
      // Inicializar las cotizaciones locales cuando se abre el modal
      setLocalSelectedQuotes([...selectedQuotes]);
      setLocalInstallmentPayments({...installmentPayments});
    }
  }, [open, selectedQuotes, installmentPayments]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await getAvailableProducts();
        console.log('Respuesta de productos:', response); // Para debug
        
        if (response && response.productKeys && Array.isArray(response.productKeys)) {
          // Convertir los strings en objetos estructurados
          const products = response.productKeys.map((productKey: string) => {
            // Parsear el string para extraer aseguradora y segmento
            const parts = productKey.split(' - ');
            let insurerKey, segmentName;
            
            if (parts.length > 1) {
              // Tiene guión: "Hdi - Básico - PT" → insurerKey: "Hdi", segmentName: "Básico - PT"
              insurerKey = parts[0]?.trim() || productKey;
              segmentName = parts.slice(1).join(' - ').trim();
            } else {
              // No tiene guión: "AXA Plus Asis. Vip" → insurerKey: "AXA", segmentName: "Plus Asis. Vip"
              const words = productKey.split(' ');
              insurerKey = words[0]?.trim() || productKey;
              segmentName = words.slice(1).join(' ').trim() || 'Estándar';
            }
            
            return {
              productKey: productKey,
              insurerKey: insurerKey,
              segmentKey: segmentName.toLowerCase().replace(/\s+/g, '_'),
              segmentName: segmentName
            };
          });
          
          setAvailableProducts(products);
        } else {
          console.error('Estructura de respuesta inesperada:', response);
          setAvailableProducts([]);
        }
      } catch (error) {
        console.error('Error al obtener productos:', error);
        setAvailableProducts([]);
      }
    };

    if (open) {
      fetchProducts();
    }
  }, [open]);

  // Función para calcular automáticamente la cuota cuando cambia el valor prima
  const calculateInstallmentPayment = async (primaValue: string) => {
    if (!primaValue || primaValue.trim() === '') {
      setManualQuote(prev => ({ ...prev, installmentPayment: '' }));
      return;
    }

    setCalculatingInstallment(true);
    try {
      const formatValueToFinanced = validAndParseNumber(primaValue);
      const { insuranceRate } = await getInsuranceRate(formatValueToFinanced);

      if (insuranceRate && insuranceRate[0]) {
        const monthlyRate = insuranceRate[0]?.interest_rate;
        const numPeriods = 11;
        const priceGateway = insuranceRate[0]?.price_gateway;

        const payment =
          (monthlyRate * formatValueToFinanced) / (1 - Math.pow(1 + monthlyRate, -numPeriods));

        const totalPayment = payment + priceGateway;

        setManualQuote(prev => ({ 
          ...prev, 
          installmentPayment: Math.round(totalPayment).toString()
        }));
      } else {
        // Si no hay tasa, usar el mismo valor dividido en 11 cuotas
        setManualQuote(prev => ({ 
          ...prev, 
          installmentPayment: Math.round(formatValueToFinanced / 11).toString()
        }));
      }
    } catch (error) {
      console.error('Error calculando cuota:', error);
      // En caso de error, calcular una estimación simple
      const formatValueToFinanced = validAndParseNumber(primaValue);
      setManualQuote(prev => ({ 
        ...prev, 
        installmentPayment: Math.round(formatValueToFinanced / 11).toString()
      }));
    } finally {
      setCalculatingInstallment(false);
    }
  };

  const handleAddManualQuote = async () => {
    if (!manualQuote.productKey || !manualQuote.value) {
      return;
    }

    setLoadingProductDetails(true);
    
    // Obtener nombre y placa de las cotizaciones existentes
    const existingQuote = localSelectedQuotes.length > 0 ? localSelectedQuotes[0] : null;
    const firstName = existingQuote?.firstName || '';
    const licenseVehicle = existingQuote?.licenseVehicle || '';
    
    try {
      // Obtener los detalles completos del producto
      const productDetailsResponse = await getProductDetails(manualQuote.productKey);
      
      if (productDetailsResponse && productDetailsResponse.producto) {
        const productDetails = productDetailsResponse.producto;
        
        // Crear la cotización con toda la información del producto
        const newQuote = {
          ...manualQuote,
          firstName: firstName, // Mapear nombre del cliente
          licenseVehicle: licenseVehicle, // Mapear placa del vehículo
          installmentPayment: manualQuote.installmentPayment || 'Calculando...',
          isManualQuote: true, // Bandera para identificar cotizaciones manuales
          // Añadir todos los detalles del producto para PDF y oferta web
          segment: {
            segment: productDetails.segment || manualQuote.segmentKey,
            extracontractualCivilLiability: productDetails.extracontractualCivilLiability || {
              visible: true,
              items: [
                { key: 'danosBienesTerceros', label: 'Daños a Bienes de Terceros', value: 'Si', visible: true },
                { key: 'muerteLesionUnaPersona', label: 'Muerte o Lesión a Una Persona', value: 'Si', visible: true },
                { key: 'muerteLesionDosOMasPersonas', label: 'Muerte o Lesión a Dos o Más Personas', value: 'Si', visible: true }
              ]
            },
            coverages: productDetails.coverages || {
              visible: true,
              items: []
            },
            assistances: productDetails.assistances || {
              visible: true,
              items: []
            },
            additionalAssistances: productDetails.additionalAssistances || {
              visible: true,
              items: []
            }
          },
          // Información adicional para compatibilidad
          insuranceDetails: productDetails.insuranceDetails || {
            nameProduct: manualQuote.title
          },
          saleforceKey: productDetails.saleforceKey || ''
        };

        const uniqueId = generateUniqueId(newQuote);
        setLocalSelectedQuotes(prev => [...prev, newQuote]);
        setLocalInstallmentPayments(prev => ({...prev, [uniqueId]: manualQuote.installmentPayment}));
        
        setShowManualQuoteForm(false);
        clearManualQuoteForm();
      } else {
        console.error('No se pudieron obtener los detalles del producto');
        alert('Error al obtener los detalles del producto. Se agregará la cotización con información básica.');
        
        const basicQuote = {
          ...manualQuote,
          firstName: firstName, 
          licenseVehicle: licenseVehicle, 
          installmentPayment: manualQuote.installmentPayment || 'Calculando...',
          isManualQuote: true,
          insuranceDetails: {
            nameProduct: manualQuote.title
          }
        };
        
        const uniqueId = generateUniqueId(basicQuote);
        setLocalSelectedQuotes(prev => [...prev, basicQuote]);
        setLocalInstallmentPayments(prev => ({...prev, [uniqueId]: manualQuote.installmentPayment}));
        
        setShowManualQuoteForm(false);
        clearManualQuoteForm();
      }
    } catch (error) {
      console.error('Error al obtener detalles del producto:', error);
      alert('Error al obtener los detalles del producto. Se agregará la cotización con información básica.');
      
      // Fallback en caso de error
      const basicQuote = {
        ...manualQuote,
        firstName: firstName, // Mapear nombre del cliente
        licenseVehicle: licenseVehicle, // Mapear placa del vehículo
        installmentPayment: manualQuote.installmentPayment || 'Calculando...',
        isManualQuote: true, // Bandera para identificar cotizaciones manuales
        // Información básica para compatibilidad
        insuranceDetails: {
          nameProduct: manualQuote.title
        }
      };
      
      const uniqueId = generateUniqueId(basicQuote);
      setLocalSelectedQuotes(prev => [...prev, basicQuote]);
      setLocalInstallmentPayments(prev => ({...prev, [uniqueId]: manualQuote.installmentPayment}));
      
      setShowManualQuoteForm(false);
      clearManualQuoteForm();
    } finally {
      setLoadingProductDetails(false);
    }
  };

  const clearManualQuoteForm = () => {
    setManualQuote({
      ...manualQuote,
      insurerKey: '',
      segmentKey: '',
      productKey: '',
      value: '',
      installmentPayment: '',
      title: ''
    });
    setCalculatingInstallment(false);
  };

  const handleCancelManualQuote = () => {
    clearManualQuoteForm();
    setShowManualQuoteForm(false);
  };

  // Función para eliminar una cotización
  const handleRemoveQuote = (quoteIndex: number) => {
    const quoteToRemove = localSelectedQuotes[quoteIndex];
    const uniqueId = generateUniqueId(quoteToRemove);
    
    // Remover la cotización del array
    setLocalSelectedQuotes(prev => prev.filter((_, index) => index !== quoteIndex));
    
    // Remover el pago a plazos correspondiente
    setLocalInstallmentPayments(prev => {
      const newPayments = { ...prev };
      delete newPayments[uniqueId];
      return newPayments;
    });
    
    // Limpiar el enlace de oferta si existe
    if (offerLink) {
      setOfferLink('');
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth={'lg'}>
        <DialogTitle className={classes.dialogTitle}>
          <img className={classes.logo} src={LogoCavca} alt="logo" />
          <Typography variant="h6" style={{ fontWeight: 'bold', display: 'inline' }}>
            Cuadro comparativo
          </Typography>
          {' / Esta cotización tiene una duración de 10 días'}
        </DialogTitle>
        <DialogContent>
          {localSelectedQuotes.length > 0 ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGenerateOffer}
                  disabled={loading}
                >
                  {loading ? 'Generando...' : 'Generar Oferta en Web'}
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={generatePDF} 
                  style={{ marginLeft: 10 }}
                  disabled={localSelectedQuotes.length > 9}
                >
                  Generar Oferta en PDF
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setShowManualQuoteForm(true)}
                  style={{ marginLeft: 10 }}
                >
                  Agregar Cotización Manual
                </Button>
                {localSelectedQuotes.length > 9 && (
                  <Typography 
                    variant="caption" 
                    color="error" 
                    style={{ display: 'block', marginTop: 8 }}
                  >
                    No se puede generar el PDF con más de 9 aseguradoras, para este caso se puede generar una oferta en web.
                  </Typography>
                )}
              </Grid>

              {showManualQuoteForm && (
                <Grid item xs={12}>
                  <Card className={classes.card}>
                    <CardHeader 
                      title="Agregar Cotización Manual" 
                      className={classes.cardHeader}
                    />
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={12} md={12}>
                          <FormControl fullWidth>
                            <InputLabel shrink>Producto</InputLabel>
                            <Select
                              value={manualQuote.productKey}
                              onChange={(e) => {
                                const products = Array.isArray(availableProducts) ? availableProducts : [];
                                const selectedProduct = products.find(p => p.productKey === e.target.value);
                                setManualQuote({
                                  ...manualQuote, 
                                  productKey: e.target.value as string,
                                  insurerKey: selectedProduct?.insurerKey || '',
                                  segmentKey: selectedProduct?.segmentKey || '',
                                  title: selectedProduct ? `${selectedProduct.insurerKey} - ${selectedProduct.segmentName}` : ''
                                });
                              }}
                              displayEmpty
                              renderValue={(selected) => {
                                if (!selected) {
                                  return <span style={{ color: '#999' }}>Seleccione un producto disponible</span>;
                                }
                                const products = Array.isArray(availableProducts) ? availableProducts : [];
                                const selectedProduct = products.find(p => p.productKey === selected);
                                return selectedProduct ? `${selectedProduct.insurerKey} - ${selectedProduct.segmentName}` : (selected as string);
                              }}
                            >
                              {Array.isArray(availableProducts) ? availableProducts.map((product) => (
                                <MenuItem key={product.productKey} value={product.productKey}>
                                  {product.insurerKey} - {product.segmentName}
                                </MenuItem>
                              )) : []}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                          <TextField
                            fullWidth
                            label="Valor Prima"
                            placeholder="Ej: $ 1.500.000"
                            type="text"
                            value={manualQuote.value ? `$ ${Number(manualQuote.value.replace(/[^0-9]/g, '')).toLocaleString('es-CO')}` : ''}
                            onChange={(e) => {
                              // Extraer solo los números del input
                              const numericValue = e.target.value.replace(/[^0-9]/g, '');
                              setManualQuote({...manualQuote, value: numericValue});
                              // Calcular automáticamente la cuota
                              if (numericValue && numericValue.trim() !== '') {
                                calculateInstallmentPayment(numericValue);
                              } else {
                                setManualQuote(prev => ({ ...prev, installmentPayment: '' }));
                              }
                            }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            helperText="Ingrese el valor de la prima anual"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                          <TextField
                            fullWidth
                            label="Valor Cuota"
                            placeholder={calculatingInstallment ? "Calculando..." : "Se calculará automáticamente"}
                            type="text"
                            value={calculatingInstallment ? "Calculando..." : (manualQuote.installmentPayment ? `$ ${Number(manualQuote.installmentPayment).toLocaleString('es-CO')}` : "")}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            InputProps={{
                              readOnly: true,
                              style: { 
                                backgroundColor: '#f5f5f5',
                                color: calculatingInstallment ? '#666' : '#333'
                              }
                            }}
                            helperText="Este valor se calcula automáticamente basado en la prima anual"
                            disabled={true}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddManualQuote}
                            disabled={!manualQuote.productKey || !manualQuote.value || loadingProductDetails}
                          >
                            {loadingProductDetails ? 'Obteniendo detalles...' : 'Agregar Cotización'}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={handleCancelManualQuote}
                            style={{ marginLeft: 10 }}
                          >
                            Cancelar
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {localSelectedQuotes.map((quote, index) => {
                console.log(quote);
                const uniqueId = generateUniqueId(quote);

                return (
                  <Grid item xs={12} sm={12} md={4} key={index}>
                    <Card className={classes.card} style={{ position: 'relative' }}>
                      <IconButton 
                        size="small"
                        onClick={() => handleRemoveQuote(index)}
                        style={{ 
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          zIndex: 1,
                          width: 28,
                          height: 28,
                          color: '#666'
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      <CardHeader 
                        title="Seguro todo riesgo auto"  
                        className={classes.cardHeader}
                        titleTypographyProps={{ variant: 'h6' }} 
                      />
    
                      <CardContent style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <div className={classes.chipContainer}>
                          <Chip 
                            label={quote?.segment?.segment || 'Sin Segmento'}
                            className={
                              quote?.segment?.segment === 'Premium'
                                ? classes.premiumChip
                                : quote?.segment?.segment === 'Basico'
                                ? classes.basicChip
                                : quote?.segment?.segment === 'Estandar'
                                ? classes.estandarChip
                                : classes.noClassChip
                            }
                              
                          /> 
                        </div>
                        <Grid container alignItems="center" justifyContent="space-evenly" style={{ flexGrow: 1 }}>
                          <Grid item>
                            <InsuranceLogoV2 insurance={quote.isManualQuote ? mapInsurerKeyToLogoKey(quote.insurerKey) : quote.insurerKey}/>
                          </Grid>
                          <Grid item>
                            <div>
                              <Typography variant="body2" align="right">Nombre: {quote.firstName}</Typography>
                              <Typography variant="body2" align="right">Placa: {quote.licenseVehicle}</Typography>
                            </div>
                          </Grid>
                        </Grid>
                        <Divider className={classes.divider} />

                        {/* Disposición del precio y pago mensual */}
                        <Grid container alignItems="center" justifyContent="center">
                          <Grid item xs={5}>
                            <Typography variant="body2" color='primary' style={{fontWeight: 'bold'}}>Pago</Typography>
                            <Typography variant="body1">Contado {formatCurrencyValueForDisplay(quote.value, quote.isManualQuote)}</Typography>
                          </Grid>

                          <Divider orientation="vertical" flexItem className={classes.verticalDivider} />

                          <Grid item xs={5}>
                            <Typography variant="body2" color='primary' style={{fontWeight: 'bold'}}>Pagos 11 Cuotas</Typography>
                            <Typography variant="body1">Mensuales {localInstallmentPayments[uniqueId] ? formatCurrencyValueForDisplay(localInstallmentPayments[uniqueId], quote.isManualQuote) : 'Calculando...'}</Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}

            {offerLink && (
                <Grid item xs={12} style={{ marginTop: 16 }} ref={offerLinkRef}>
                  <Divider className={classes.divider} style={{ margin: '10px 0' }} />
                  <Typography variant="h6" color="primary">Enlace de oferta generado:</Typography>
                  <Grid container alignItems="center">
                    <Grid item xs={10}>
                      <TextField
                        value={offerLink}
                        variant="outlined"
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <IconButton onClick={() => handleCopyLink(offerLink)}>
                        <CopyIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              
            </Grid>
          ) : (
            <Typography variant="body2">No hay cotizaciones seleccionadas.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuotePreviewModal;
